import { useEffect, useRef } from 'react'

/**
 * Refetch a tab's shared lists when it comes back into view.
 *
 * Panes stay mounted across tab switches so they keep their state (see the
 * comment in App.tsx). The cost of that is staleness across tabs: saving a
 * run in People used to make Campaign's list picker refetch, because Campaign
 * remounted. Now it does not, and a run saved a moment ago would be missing
 * from the picker that exists to choose it.
 *
 * So: keep the local state, refresh the server's. This runs on re-activation
 * only — never on the first mount, where the pane's own effect already
 * loaded — and a loader must therefore not reset selections, only replace the
 * list it owns.
 */
export function useRefreshOnActivate(active: boolean, load: () => void | Promise<void>) {
  const wasActive = useRef(active)
  const latest = useRef(load)
  latest.current = load

  useEffect(() => {
    if (active && !wasActive.current) void latest.current()
    wasActive.current = active
  }, [active])
}
